import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
  ViewEncapsulation,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import { User, UserCredential } from 'firebase/auth';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NGX_AUTH_FIREBASEUI_ANIMATIONS } from '../animations';
import { AuthProcessService, AuthProvider } from '../auth-process.service';
import { Layout, MaterialButtonType } from '../auth-interfaces';

@Component({
  selector: 'pt-auth-firebaseui-login',
  templateUrl: './ngx-auth-firebaseui-login.component.html',
  styleUrls: ['./ngx-auth-firebaseui-login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: NGX_AUTH_FIREBASEUI_ANIMATIONS,
  standalone: false,
  providers: [AuthProcessService],
})
export class NgxAuthFirebaseuiLoginComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();

  theme: MaterialButtonType = MaterialButtonType.RAISED;
  layout: Layout = Layout.COLUMN;

  @Input() logoUrl: string;
  @Input() providers: AuthProvider | AuthProvider[] = AuthProvider.ALL;
  @Input() appearance: MatFormFieldAppearance;
  @Input() registrationEnabled = true;
  @Input() resetPasswordEnabled = true;
  @Input() messageOnAuthSuccess: string;
  @Input() messageOnAuthError: string;

  @Input() titleText = 'LOGIN TO YOUR ACCOUNT';
  @Input() rememberMeText = 'Remember Me';
  @Input() loginButtonText = 'LOGIN';
  @Input() orLabelText = 'OR';
  @Input() forgotPasswordText = 'Forgot Password?';
  @Input() dontHaveAnAccountText = `Don't have an account?`;
  @Input() createAccountButtonText = 'Create an account';

  @Input() emailText = 'Email';
  @Input() emailErrorRequiredText = 'Email is required';
  @Input() emailErrorPatternText = 'Please enter a valid email address';

  @Input() passwordText = 'Password';
  @Input() passwordErrorRequiredText = 'Password is required';

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSuccess: EventEmitter<UserCredential | User>;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onError: EventEmitter<Error>;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onCreateAccountRequested = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onResetPasswordRequested = new EventEmitter<void>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onLoginButtonClicked = new EventEmitter<void>();

  loginForm = new TypedFormGroup({
    email: new TypedFormControl('', [Validators.required, Validators.email]),
    password: new TypedFormControl('', Validators.required),
  });

  authenticationError = false;

  constructor(
    @Inject(PLATFORM_ID) private _platformId: object,
    public authProcess: AuthProcessService
  ) {
    this.onSuccess = authProcess.onSuccessEmitter;
    this.onError = authProcess.onErrorEmitter;

    if (isPlatformBrowser(this._platformId)) {
      this.onError
        .pipe(takeUntil(this._onDestroy$))
        .subscribe(() => (this.authenticationError = true));
    }

    this.updateAuthSnackbarMessages();
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  get color(): string {
    return this.authenticationError ? 'warn' : 'primary';
  }

  get colorAccent(): string {
    return this.authenticationError ? 'warn' : 'accent';
  }

  public updateAuthSnackbarMessages(): void {
    this.authProcess.messageOnAuthSuccess = this.messageOnAuthSuccess;
    this.authProcess.messageOnAuthError = this.messageOnAuthError;
  }

  async login(): Promise<void> {
    this.onLoginButtonClicked.emit();

    await this.authProcess.signInWith(AuthProvider.EmailAndPassword, {
      email: this.loginForm.controls.email.value,
      password: this.loginForm.controls.password.value,
    });
  }
}
